/* eslint-disable @typescript-eslint/no-use-before-define */
import { useMemo } from 'react';
import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, type IGatsbyImageData } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { SPACING } from '../design-system/RunningText';

const Figure = styled.figure`
  margin-bottom: ${SPACING}px;

  [data-gatsby-image-wrapper],
  img {
    margin: 0 auto;
  }
`;

interface InlineImageProps {
  alt?: string;
  caption?: string;
  gatsbyImageData: IGatsbyImageData;
  name?: string;
  src?: string;
}

export const InlineImage: React.ComponentType<InlineImageProps> = React.memo(
  ({ alt, caption, name, src, gatsbyImageData }) => {
    const data = useStaticQuery(pageQuery);

    const imgPath = useMemo(() => {
      if (src) {
        return src;
      } else {
        const allImages = [...data.allContentfulAsset.edges];
        const thisImage = allImages.filter(image => image.node.title === name);
        return thisImage ? thisImage[0].node.url : undefined;
      }
    }, [data.allContentfulAsset.edges, name, src]);

    if (!imgPath) {
      return null;
    }

    return (
      <Figure>
        {gatsbyImageData ? (
          <GatsbyImage
            alt={caption || alt || ''}
            image={gatsbyImageData}
            sizes="(min-width: 1010px) 740px, 100vw"
          />
        ) : (
          <img alt={caption || alt} loading="lazy" src={imgPath} />
        )}
        <figcaption>{caption}</figcaption>
      </Figure>
    );
  }
);

// Anyone know where/why we're doing this?
// Seems aggressive to pull in every image metadata
// on every blog post. Would be nice to use gatsbyImageData
// here but I think that'd only worsen the amount of data we
// are pulling in here.
const pageQuery = graphql`
  query {
    allContentfulAsset(
      filter: { mimeType: { in: ["image/jpeg", "image/png", "image/gif"] } }
    ) {
      edges {
        node {
          title
          url
        }
      }
    }
  }
`;
